import {memo, useMemo} from 'react'
import {StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {type AppBskyActorDefs, type ModerationDecision} from '@atproto/api'
import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {
  type ClassroomPostAudience,
  type ClassroomPostMark,
  type ClassroomRecordAnswer,
  classroomRoute,
  currentClassroomMark,
  isMembersOnlyClassroomPost,
  isTheWholeClassroom,
  liveClassroomPostMark,
  mayAnyoneRead,
  postAudience,
  readClassroomPostMark,
} from '#/state/classrooms'
import {ClassroomApiError, useClassroomQuery} from '#/state/queries/classrooms'
import {PostMeta} from '#/view/com/util/PostMeta'
import {PreviewableUserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {postSeatWord} from '#/components/classrooms/postAudienceNames'
import {Lock_Stroke2_Corner2_Rounded as LockIcon} from '#/components/icons/Lock'
import {LinearGradientBackground} from '#/components/LinearGradientBackground'
import {InlineLinkText, Link} from '#/components/Link'
import {Text} from '#/components/Typography'

const COVER_SIZE = 42
const AVATAR_SIZE = 22

/**
 * The room's own page. Both the cover and the room's name lead there: a post
 * written in a room is the room's, so its byline is a way into the room.
 */
function classroomHref(mark: ClassroomPostMark): string {
  /*
   * The school, then the room's own handle. Marks written before the school
   * rode on them carry no owner, and those cannot name a room the server can
   * find; the room's handle stands in for the owner so the link still has a
   * shape.
   */
  return classroomRoute({
    ownerHandle: mark.ownerHandle ?? mark.handle,
    handle: mark.handle,
  })
}

/**
 * What the app says instead of a post the reader is not in the audience for.
 *
 * One sentence, and which sentence it is comes from the post's own answer
 * (`postAudience`): a card that said *only members* over a post written for the
 * room's teachers would be wrong about the very thing the author decided, and it
 * would send a member looking for a way in that does not exist. Everything that
 * is not a seat — a private room's post, a post addressed to the whole room — is
 * the same sentence, because *only members* is the room's widest promise about
 * work that is not everybody's.
 *
 * The room's own name is deliberately not in it, and neither is a way in: the
 * card is shown to somebody who is not in the audience, and asking a school for
 * the code is an answer the byline already gives by naming the room's page.
 */
function lockedSentence(
  _: (descriptor: MessageDescriptor) => string,
  audience: ClassroomPostAudience,
): string {
  /*
   * The whole room reads as the room, and so does an audience read off a record
   * written before the seats were a list: *members* is every seat, which is the
   * sentence this card has always used for it.
   */
  if (mayAnyoneRead(audience) || isTheWholeClassroom(audience)) {
    return _(msg`Only members of this classroom can see this post.`)
  }
  /*
   * One seat keeps its own sentence, written out whole rather than assembled — it
   * is the sentence a reader meets most often here, and a language should be able
   * to say it its own way (*the teachers of this classroom*, *its teachers*).
   */
  if (audience.length === 1) {
    switch (audience[0]) {
      case 'teachers':
        return _(msg`Only the teachers in this classroom can see this post.`)
      case 'parents':
        return _(msg`Only the parents in this classroom can see this post.`)
      case 'students':
        return _(msg`Only the students in this classroom can see this post.`)
    }
  }
  /*
   * Two seats are named together, and the words for them are the same words the
   * rows and the pill use (`postSeatWord`) — so the card cannot say *parents* to
   * somebody the composer just called something else.
   */
  const first = postSeatWord(_, audience[0])
  const second = postSeatWord(_, audience[1])
  return _(
    msg`Only the ${first} and ${second} in this classroom can see this post.`,
  )
}

/**
 * A post written in a classroom, introduced as the *room's*.
 *
 * The byline a feed draws for an ordinary post says *who* wrote it. A post
 * written in a room has two answers to that question, and the room is the one
 * that matters: the post is the room's, and the person is its author. So the
 * room's cover is the square the avatar column holds, the author's picture is
 * the circle on its corner, and the room's name sits where a display name sits
 * — with the publisher on the line beneath, exactly as a group post reads.
 *
 * The cover is drawn only when the room is on this device, because that is the
 * only place it exists (see `#/lib/media/coverPicture`); a reader whose device
 * has never heard of the room gets the same gradient a room without a cover
 * starts life with, which is truer than a blank square.
 *
 * ## A post whose classroom was deleted
 *
 * A post outlives its classroom: the record is the author's, and nothing about
 * ending a room takes the posts written in it away. What it takes away is the
 * *room's* part of the post — so the post becomes an ordinary post again, the
 * author's own, drawn with the avatar and byline every other post gets and with
 * no cover, no room name and no sign that a classroom was ever part of it. That
 * decision is made once, where the mark is read (`useLiveClassroomPostMark`),
 * and this component is only ever handed a mark for a room that still exists.
 */

/**
 * The classroom a post says it was written in, as it should be read *now*.
 *
 * The rule itself is `liveClassroomPostMark` in the classrooms' own rules
 * module — a public room that was ended leaves no mark, so every list that draws
 * the post draws it the way it draws any other post, and a private room's mark
 * survives its room. What the hook adds is the answer the rule is asked with:
 * the room as the server answers it (`useRoomRecord`), read for
 * every room a post names.
 *
 * Asked of every mark that names a school, including one for a room this device
 * holds: a copy of a deleted room that a device happens to keep would otherwise
 * keep the room's name on every post it ever made (`deleteClassroom` forgets the
 * copy when the room's page is opened, but a post can be read before that), and
 * the answer must be the same everywhere. The school an old mark does not carry
 * is lent by the held room (`currentClassroomMark` does the same for its name),
 * and a room that was never published — no record at all — reads `unknown` and
 * keeps its mark.
 *
 * A mark written before the school rode on the record, read on a device that does
 * **not** hold the room, is the one case with no address to ask: the read answers
 * `idle` and the rule draws the post as its author's own
 * (`liveClassroomPostMark`). Nothing at all is known about that room there —
 * whether it is still up, whether it ever was — and the byline the mark would draw
 * opens the room's slug, which lands only on a device that already has the room: a
 * reach the reader cannot make. So only the devices that hold the room keep such a
 * mark's byline, and there the device knows the room for itself.
 */
export function useLiveClassroomPostMark(post: {
  record?: unknown
}): ClassroomPostMark | null {
  const mark = useMemo(() => readClassroomPostMark(post), [post])
  const {status} = useRoomRecord(mark?.id)
  return liveClassroomPostMark(mark, {record: {status}})
}

/**
 * The room a mark names, read from the server, and the answer the mark rules
 * are asked with: a room the server says is not found was ended, a read that
 * failed any other way is not known, and a read in flight is still checking.
 */
function useRoomRecord(id: string | undefined) {
  const query = useClassroomQuery(id)
  const classroom = query.data?.classroom
  let status: ClassroomRecordAnswer
  if (!id) status = 'idle'
  else if (classroom) status = 'here'
  else if (query.error)
    status =
      query.error instanceof ClassroomApiError &&
      query.error.code === 'NotFound'
        ? 'gone'
        : 'unknown'
  else status = 'checking'
  return {classroom, status}
}
export let ClassroomPostByline = ({
  mark,
  author,
  moderation,
  timestamp,
  postHref,
  onOpenAuthor,
}: {
  mark: ClassroomPostMark
  author: AppBskyActorDefs.ProfileViewBasic
  moderation: ModerationDecision
  timestamp: string
  postHref: string
  onOpenAuthor?: () => void
}): React.ReactNode => {
  const t = useTheme()
  const {t: l} = useLingui()
  const {classroom} = useRoomRecord(mark.id)
  const current = currentClassroomMark(mark, classroom)
  const cover = classroom?.coverUrl ?? classroom?.cover

  return (
    <View style={[a.flex_row, a.align_center, a.gap_sm]}>
      <Link
        to={classroomHref(current)}
        label={l`Open ${current.name}`}
        style={[
          styles.cover,
          t.atoms.bg_contrast_25,
          current.visibility === 'private' && styles.coverBorder,
        ]}>
        {cover ? (
          <Image
            accessibilityIgnoresInvertColors
            source={{uri: cover}}
            style={styles.coverFill}
            contentFit="cover"
          />
        ) : (
          <LinearGradientBackground gradient="sky" style={styles.coverFill} />
        )}
      </Link>
      <View style={[styles.corner, {borderColor: t.atoms.bg.backgroundColor}]}>
        <PreviewableUserAvatar
          size={AVATAR_SIZE}
          profile={author}
          moderation={moderation.ui('avatar')}
          type={author.associated?.labeler ? 'labeler' : 'user'}
        />
      </View>

      <View style={[a.flex_1, a.justify_center]}>
        <View style={[a.flex_row, a.align_center, a.gap_xs]}>
          <InlineLinkText
            emoji
            numberOfLines={1}
            to={classroomHref(current)}
            label={l`Open ${current.name}`}
            disableMismatchWarning
            style={[
              a.text_md,
              a.font_semi_bold,
              a.leading_tight,
              t.atoms.text,
            ]}>
            {current.name}
          </InlineLinkText>
          {isMembersOnlyClassroomPost(current) && (
            <View
              testID="classroomPostBylineLock"
              accessible
              accessibilityLabel={lockedSentence(l, postAudience(current))}>
              <LockIcon size="xs" style={{color: t.palette.contrast_400}} />
            </View>
          )}
        </View>
        <PostMeta
          author={author}
          moderation={moderation}
          timestamp={timestamp}
          postHref={postHref}
          onOpenAuthor={onOpenAuthor}
          style={[a.pt_2xs]}
        />
      </View>
    </View>
  )
}
ClassroomPostByline = memo(ClassroomPostByline)

/**
 * What a reader who is not in the room is told instead of its post.
 *
 * A private room's post is the room's alone, and AT Protocol has no lock to
 * put on a record — so the app says the rule in the reader's own language
 * rather than pretending the post is missing. It is addressed to the reader:
 * they are shown which room the post belongs to, that it is for its members,
 * and the room's name, so the way in — asking the school for the room's code —
 * is something they can act on.
 *
 * A room that was deleted has no way in, and nothing to promise: the card keeps
 * the lock — *private* was the author's promise, and a room ending does not
 * publish what was written in it — but drops the room, because there is no room
 * left to name, no page to open and no code to ask for. What is left is the
 * author's own byline and one honest line, the app's own words for a post that
 * cannot be shown.
 *
 * The audience the author chose is the third reason a reader meets this card, and
 * the rule is worked out before it (`isClassroomPostVisibleTo`): a public room's
 * post written for the room's teachers is nobody else's, and a member who is not
 * a teacher is told the rule rather than shown the work. What changes for them is
 * one word — the line names the seat the post was written for — because *only
 * members* would be untrue about a post its own room's members cannot read.
 */
export let ClassroomPostLocked = ({
  mark,
  author,
  moderation,
  timestamp,
  postHref,
}: {
  mark: ClassroomPostMark
  author: AppBskyActorDefs.ProfileViewBasic
  /*
   * A message's card draws a post by its record alone, with no moderation
   * decision of its own to hand (`MessageItemEmbed`), and the card this draws
   * says nothing a decision would have to censor: the author's own name and a
   * line the app wrote itself. So the decision is optional here, and made when
   * the surface has one (`Post`, `PostFeedItem`, `ThreadItemAnchor`).
   */
  moderation?: ModerationDecision
  timestamp: string
  postHref: string
}): React.ReactNode => {
  const t = useTheme()
  const {t: l} = useLingui()
  const {classroom, status} = useRoomRecord(mark.id)
  const current = currentClassroomMark(mark, classroom)
  const cover = classroom?.coverUrl ?? classroom?.cover
  /* Only the server saying the room is not found means it was *ended*. */
  const gone = status === 'gone'

  if (gone) {
    return (
      <View
        testID="classroomPostLocked"
        style={[a.flex_row, a.align_center, a.gap_sm]}>
        <PreviewableUserAvatar
          size={42}
          profile={author}
          moderation={moderation?.ui('avatar')}
          type={author.associated?.labeler ? 'labeler' : 'user'}
        />
        {/* The author's own column, the way an ordinary post lays it out. */}
        <View style={[a.flex_1]}>
          <PostMeta
            author={author}
            moderation={moderation}
            timestamp={timestamp}
            postHref={postHref}
          />
          <Text
            style={[
              a.text_sm,
              a.leading_snug,
              a.pt_2xs,
              t.atoms.text_contrast_medium,
            ]}>
            {l`This post is no longer here.`}
          </Text>
        </View>
      </View>
    )
  }

  return (
    <View
      testID="classroomPostLocked"
      style={[a.flex_row, a.align_center, a.gap_sm]}>
      <Link
        to={classroomHref(current)}
        label={l`Open ${current.name}`}
        style={[styles.cover, t.atoms.bg_contrast_25]}>
        {cover ? (
          <Image
            accessibilityIgnoresInvertColors
            source={{uri: cover}}
            style={styles.coverFill}
            contentFit="cover"
          />
        ) : (
          <LinearGradientBackground gradient="sky" style={styles.coverFill} />
        )}
      </Link>
      <View style={[styles.corner, {borderColor: t.atoms.bg.backgroundColor}]}>
        <PreviewableUserAvatar
          size={AVATAR_SIZE}
          profile={author}
          type={author.associated?.labeler ? 'labeler' : 'user'}
        />
      </View>

      <View style={[a.flex_1]}>
        <View style={[a.flex_row, a.align_center, a.gap_xs]}>
          <InlineLinkText
            emoji
            numberOfLines={1}
            to={classroomHref(current)}
            label={l`Open ${current.name}`}
            disableMismatchWarning
            style={[
              a.text_md,
              a.font_semi_bold,
              a.leading_tight,
              t.atoms.text,
            ]}>
            {current.name}
          </InlineLinkText>
          <LockIcon size="xs" style={{color: t.palette.contrast_400}} />
        </View>
        <Text
          style={[
            a.text_sm,
            a.leading_snug,
            a.pt_2xs,
            t.atoms.text_contrast_medium,
          ]}>
          {lockedSentence(l, postAudience(current))}
        </Text>
      </View>
    </View>
  )
}
ClassroomPostLocked = memo(ClassroomPostLocked)

const styles = StyleSheet.create({
  cover: {
    width: COVER_SIZE,
    height: COVER_SIZE,
    borderRadius: 10,
    overflow: 'hidden',
  },
  coverFill: {
    width: '100%',
    height: '100%',
  },
  coverBorder: {
    borderWidth: StyleSheet.hairlineWidth,
  },
  /**
   * The author's circle, hung on the cover's corner as a group post hangs it.
   *
   * The border is the page's own background pretending to be a ring: the
   * avatar is placed so its right side reaches past the square's edge, and the
   * ring is what keeps the two pictures from reading as one.
   */
  corner: {
    position: 'absolute',
    left: COVER_SIZE - AVATAR_SIZE + 4,
    top: COVER_SIZE - AVATAR_SIZE / 2,
    borderRadius: AVATAR_SIZE,
    borderWidth: 2,
  },
})
